/**
 * Checkpoint lineage and lifecycle activity for one model.
 *
 * Both are DERIVED reads, not stored records: checkpoints come from the
 * distinct `model_slug`s a pipeline scored on this model's eval surface,
 * and activity composes the model's creation, those scores, and the
 * gateway's outage probes into one dated feed. Nothing here needs a write
 * path — see `api/app/services/checkpoints.py`.
 */

import type { ApiRequestError } from '@/api/client'
import {
  useGetModelActivityApiModelsModelIdActivityGet,
  useGetModelCheckpointsApiModelsModelIdCheckpointsGet,
} from '@/api/generated/models/models'
import type {
  CheckpointsResponse,
  ModelActivityResponse,
} from '@/api/generated/model'

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  isError: boolean
  error: ApiRequestError | null
}

export function useCheckpoints(
  modelId: string | undefined,
): UnwrappedQuery<CheckpointsResponse> {
  const query = useGetModelCheckpointsApiModelsModelIdCheckpointsGet(
    modelId ?? '',
    { query: { enabled: !!modelId } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  }
}

export function useModelActivity(
  modelId: string | undefined,
): UnwrappedQuery<ModelActivityResponse> {
  const query = useGetModelActivityApiModelsModelIdActivityGet(modelId ?? '', {
    query: { enabled: !!modelId },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  }
}
